import { useState, useCallback } from 'react';
import { adminUsersAPI } from '../../services/api';

/**
 * useUserAdmin - User management state + handlers with server-side pagination.
 * Backend: GET /api/admin/users?skip=&limit=&q= returns { users, total, limit, skip, has_more }
 * when any of skip/limit/q is set. We always set them so we get the envelope.
 */
export const useUserAdmin = () => {
  const [users, setUsers] = useState([]);
  const [userStats, setUserStats] = useState({
    total: 0, google_users: 0, email_users: 0, users_with_assessments: 0, recent_users: 0,
  });
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [showUserDetailModal, setShowUserDetailModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editUserData, setEditUserData] = useState({ name: '', newPassword: '' });
  const [userDetailStats, setUserDetailStats] = useState(null);
  const [loadingUserDetail, setLoadingUserDetail] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [newUserData, setNewUserData] = useState({ email: '', name: '', password: '', role: 'user' });
  const [creatingUser, setCreatingUser] = useState(false);

  // Pagination state
  const [userPage, setUserPage] = useState(0);          // 0-indexed
  const [userPageSize, setUserPageSize] = useState(25);
  const [userTotal, setUserTotal] = useState(0);
  const [userHasMore, setUserHasMore] = useState(false);

  const fetchUsers = useCallback(async (overrides = {}) => {
    setUsersLoading(true);
    try {
      const page = overrides.page ?? userPage;
      const pageSize = overrides.pageSize ?? userPageSize;
      const q = overrides.q ?? userSearchTerm;
      const signal = overrides.signal; // AbortController.signal — optional
      const params = { skip: page * pageSize, limit: pageSize };
      if (q && q.trim()) params.q = q.trim();

      const [resp, statsData] = await Promise.all([
        adminUsersAPI.getAll(params, { signal }),
        adminUsersAPI.getStats(),
      ]);
      // Backend returns envelope when skip/limit/q present
      const usersData = Array.isArray(resp) ? resp : (resp?.users || []);
      const total = Array.isArray(resp) ? usersData.length : (resp?.total ?? usersData.length);
      const hasMore = Array.isArray(resp)
        ? false
        : (resp?.has_more ?? (page * pageSize + usersData.length) < total);

      setUsers(usersData);
      setUserTotal(total);
      setUserHasMore(hasMore);
      setUserStats(statsData || { total: 0, google_users: 0, email_users: 0, users_with_assessments: 0, recent_users: 0 });
    } catch (error) {
      // Silently ignore aborted requests (race-condition guard for debounced search)
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED' || error?.message === 'canceled') {
        return;
      }
      console.error('Failed to fetch users:', error);
    } finally {
      setUsersLoading(false);
    }
  }, [userPage, userPageSize, userSearchTerm]);

  const goToPage = useCallback((newPage) => {
    setUserPage(newPage);
    fetchUsers({ page: newPage });
  }, [fetchUsers]);

  const changePageSize = useCallback((newSize) => {
    setUserPageSize(newSize);
    setUserPage(0);
    fetchUsers({ page: 0, pageSize: newSize });
  }, [fetchUsers]);

  const handleCreateUser = useCallback(async () => {
    if (!newUserData.email || !newUserData.name || !newUserData.password) {
      alert('Vul alle velden in');
      return;
    }
    setCreatingUser(true);
    try {
      await adminUsersAPI.create(newUserData);
      setShowCreateUserModal(false);
      setNewUserData({ email: '', name: '', password: '', role: 'user' });
      fetchUsers();
    } catch (error) {
      console.error('Failed to create user:', error);
      alert(error.response?.data?.detail || 'Fout bij aanmaken gebruiker');
    } finally {
      setCreatingUser(false);
    }
  }, [newUserData, fetchUsers]);

  const handleDeleteUser = useCallback(async (userId) => {
    if (!window.confirm('Weet u zeker dat u deze gebruiker wilt verwijderen? De quickscans blijven behouden maar worden losgekoppeld.')) return;
    try {
      await adminUsersAPI.delete(userId);
      setUsers((prev) => prev.filter((u) => u.user_id !== userId));
      setUserStats((prev) => ({ ...prev, total: prev.total - 1 }));
      setUserTotal((prev) => Math.max(0, prev - 1));
      alert('Gebruiker succesvol verwijderd');
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert('Fout bij verwijderen: ' + (error.response?.data?.detail || 'Onbekende fout'));
    }
  }, []);

  const handleViewUserDetail = useCallback(async (user) => {
    setSelectedUser(user);
    setShowUserDetailModal(true);
    setLoadingUserDetail(true);
    try {
      const stats = await adminUsersAPI.getUserStats(user.user_id);
      setUserDetailStats(stats);
    } catch (error) {
      console.error('Failed to fetch user stats:', error);
      setUserDetailStats(null);
    } finally {
      setLoadingUserDetail(false);
    }
  }, []);

  const handleEditUser = useCallback((user) => {
    setSelectedUser(user);
    setEditUserData({ name: user.name || '', newPassword: '' });
    setShowEditUserModal(true);
  }, []);

  const handleSaveUserEdit = useCallback(async () => {
    if (!selectedUser) return;
    try {
      await adminUsersAPI.updateUser(selectedUser.user_id, {
        name: editUserData.name,
        new_password: editUserData.newPassword || null,
      });
      setUsers((prev) => prev.map((u) => (u.user_id === selectedUser.user_id ? { ...u, name: editUserData.name } : u)));
      setShowEditUserModal(false);
      alert('Gebruiker succesvol bijgewerkt' + (editUserData.newPassword ? ' (wachtwoord gereset)' : ''));
    } catch (error) {
      console.error('Failed to update user:', error);
      alert('Fout bij bijwerken: ' + (error.response?.data?.detail || 'Onbekende fout'));
    }
  }, [selectedUser, editUserData]);

  return {
    users, setUsers, userStats, usersLoading,
    selectedUser, setSelectedUser, userSearchTerm, setUserSearchTerm,
    showUserDetailModal, setShowUserDetailModal,
    showEditUserModal, setShowEditUserModal,
    editUserData, setEditUserData,
    userDetailStats, loadingUserDetail,
    showCreateUserModal, setShowCreateUserModal,
    newUserData, setNewUserData,
    creatingUser,
    fetchUsers,
    handleCreateUser,
    handleDeleteUser,
    handleViewUserDetail,
    handleEditUser,
    handleSaveUserEdit,
    // Pagination
    userPage, userPageSize, userTotal, userHasMore,
    goToPage, changePageSize,
  };
};
